/**
 * Page keys classified by product brand route. Keep aligned with
 * backend/app/core/page_brands.py (see page-brand-registry.test.ts).
 */
import { FRONTEND_PAGE_KEYS } from './frontend-page-keys'

export type PageBrand = 'wysehub' | 'aiclaims' | 'global'

export const WYSEHUB_PAGE_KEYS = [
  'homehub',
  'claimshire',
  'kpidashboard',
  'highlevelstats',
  'request',
  'referral-details',
  'source-rates',
  'rates',
] as const

export const AICLAIMS_PAGE_KEYS = [
  'aiclaims-dashboard',
  'aiclaims-cases',
  'aiclaims-workflows',
  'aiclaims-runs',
  'aiclaims-call-analysis',
  'aiclaims-call-auditing',
  'aiclaims-demo',
] as const

export const GLOBAL_PAGE_KEYS = [
  'admin',
  'settings',
  'demo',
  'auth-test',
  'admin-users',
  'admin-role-allocations',
  'admin-role-permissions',
  'admin-invoices',
  'admin-invoice-processing',
  'admin-user-partners',
  'admin-partners',
  'admin-partner-rules',
  'admin-flow-triggers',
  'admin-ai-prompt-config',
  'admin-report-templates',
  'admin-rate-limiting',
  'admin-app-settings',
  'admin-privacy-settings',
  'admin-metabase-reports',
  'admin-task-scheduler',
  'admin-schedule-builder',
  'admin-database',
  'admin-workflows',
  'admin-run-inspector',
  'admin-workflow-compare',
  'admin-workflow-feedback-runs',
  'admin-partner-issue-insights',
  'admin-bulk-summaries',
  'admin-security-tests',
] as const

const _wysehubSet = new Set<string>(WYSEHUB_PAGE_KEYS)
const _aiclaimsSet = new Set<string>(AICLAIMS_PAGE_KEYS)
const _globalSet = new Set<string>(GLOBAL_PAGE_KEYS)

export const PAGE_BRAND_BY_KEY: Record<string, PageBrand> = {}
for (const key of WYSEHUB_PAGE_KEYS) {
  PAGE_BRAND_BY_KEY[key] = 'wysehub'
}
for (const key of AICLAIMS_PAGE_KEYS) {
  PAGE_BRAND_BY_KEY[key] = 'aiclaims'
}
for (const key of GLOBAL_PAGE_KEYS) {
  PAGE_BRAND_BY_KEY[key] = 'global'
}

export function getPageBrand(pageKey: string): PageBrand | undefined {
  return PAGE_BRAND_BY_KEY[pageKey]
}

export function pageKeysForBrand(brand: PageBrand): readonly string[] {
  if (brand === 'wysehub') return WYSEHUB_PAGE_KEYS
  if (brand === 'aiclaims') return AICLAIMS_PAGE_KEYS
  return GLOBAL_PAGE_KEYS
}

export const PAGE_BRAND_SECTIONS: ReadonlyArray<{
  id: PageBrand
  label: string
}> = [
  { id: 'wysehub', label: 'WyseHub portal' },
  { id: 'aiclaims', label: 'AiClaims portal' },
  { id: 'global', label: 'Global (shared)' },
]

export function brandsAllowedForBrandAccess(
  brandAccess: string | null | undefined,
): Set<PageBrand> {
  const ba = (brandAccess || 'wysehub').toLowerCase()
  if (ba === 'both') {
    return new Set(['wysehub', 'aiclaims', 'global'])
  }
  if (ba === 'aiclaims') {
    return new Set(['aiclaims', 'global'])
  }
  return new Set(['wysehub', 'global'])
}

export function isPageAllowedForBrandAccess(
  pageKey: string,
  brandAccess: string | null | undefined,
): boolean {
  const pageBrand = getPageBrand(pageKey)
  if (!pageBrand) return false
  return brandsAllowedForBrandAccess(brandAccess).has(pageBrand)
}

export function filterPagesByBrandAccess(
  pageKeys: readonly string[],
  brandAccess: string | null | undefined,
): string[] {
  return pageKeys.filter((key) => isPageAllowedForBrandAccess(key, brandAccess))
}

/** Registry must classify every frontend page key. */
export function assertRegistryComplete(): void {
  const registryKeys = new Set(Object.keys(PAGE_BRAND_BY_KEY))
  for (const key of FRONTEND_PAGE_KEYS) {
    if (!registryKeys.has(key)) {
      throw new Error(`Missing page brand for key: ${key}`)
    }
  }
  if (registryKeys.size !== FRONTEND_PAGE_KEYS.length) {
    throw new Error('PAGE_BRAND_BY_KEY has keys not in FRONTEND_PAGE_KEYS')
  }
}
